import { useEffect, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { addToast } from '@/app/slices/toast';
import { AccountAutoComplete, StorageLocationAutoComplete } from '@/components';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { EXPORT_ENUMS, SEX } from '@/constants';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import type { Sire } from '@/rtk-query.gql';
import { AccountResponse } from '@/services/accounts';
import { useAddSemen } from '@/services/semen-service/hooks';
import {
  cssCertifiedOptions,
  SemenTypeOptions,
  sexTypeOptions,
} from '@/types/common';

import { AdvancedAccountLookup } from '../advanced-account-lookup';
import { AdvancedSireLookup } from '../advanced-sire-lookup';
import { createSemenFormSchema, SemenFormValues } from './schema';
import { NewSemenFormProps } from './types';

const CONVENTIONAL_SEMEN_TYPE = 'Conv';

const getSexFromSemenSexType = (
  sexType?: 'Male' | 'Female' | 'Conventional',
  useConventionalType = false,
) => {
  if (sexType === 'Male') return SEX.Male;
  if (sexType === 'Female') return SEX.Female;
  if (sexType === 'Conventional') {
    return useConventionalType ? SEX.Unknown : undefined;
  }
  return undefined;
};

export const NewSemenForm = ({
  initialValues,
  onCancel,
  onSuccess,
  onError,
  calendarContainer,
}: NewSemenFormProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: addSemen } = useAddSemen();
  const isSemenDataPointsEnabled = useFeatureFlag('Semen_Data_Points');
  const isSemenTypeLabelEnabled = useFeatureFlag('Semen_Type_Field_Label');
  const isSemenConventionalTypeEnabled = useFeatureFlag(
    'Semen_Conventional_Type',
  );
  const isConditionalValidation = useFeatureFlag(
    'Semen_Conditional_Validation',
  );
  const isAlternateAccountLookupEnabled = useFeatureFlag(
    'Alternate_Account_Lookup',
  );

  const schema = useMemo(
    () =>
      createSemenFormSchema(isConditionalValidation, isSemenTypeLabelEnabled),
    [isConditionalValidation, isSemenTypeLabelEnabled],
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SemenFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      dateReceived: new Date(),
      sire: undefined,
      owner: undefined,
      colCode: undefined,
      storageLocation: undefined,
      quantityReceived: undefined,
      cane: undefined,
      naab: undefined,
      sexType: undefined,
      cssCertified: undefined,
      export: undefined,
      center: undefined,
      tankCaneLocation: undefined,
      shortName: undefined,
      soldTo: undefined,
      secondaryOwner: undefined,
      semenType: undefined,
      units: undefined,
      country: undefined,
      comment: undefined,
      ...initialValues,
    },
  });

  const watchedSire = watch('sire');
  const watchedExport = watch('export');
  const watchedSexType = watch('sexType');

  const isNaabRequired =
    !isConditionalValidation ||
    watchedSexType === 'Male' ||
    watchedSexType === 'Female';

  useEffect(() => {
    if (watchedSire && watchedSire.name) {
      setValue('shortName', watchedSire.name);
    }
  }, [watchedSire, setValue]);

  const getExportStatusLabel = (
    exportValue: number | undefined,
  ): string | null => {
    if (exportValue === undefined || exportValue === null) {
      return null;
    }
    const entry = Object.entries(EXPORT_ENUMS).find(
      ([, value]) => value === exportValue,
    );
    return entry ? entry[0] : null;
  };

  const handleStorageLocationSelect = (value: unknown) => {
    if (
      watchedExport !== undefined &&
      watchedExport !== null &&
      watchedExport !== 0
    ) {
      const exportLabel = getExportStatusLabel(watchedExport);

      if (exportLabel && value) {
        const storageLocation = value as { name?: string };
        const storageLocationName = storageLocation.name || '';

        if (
          !storageLocationName.toLowerCase().includes(exportLabel.toLowerCase())
        ) {
          dispatch(
            addToast({
              severity: 'warn',
              summary: t('storageLocation.storageLocationWarning'),
              detail: t('storageLocation.storageLocationMismatch', {
                location: storageLocationName,
                status: exportLabel,
              }),
              life: ToastLife.MEDIUM,
            }),
          );
        }
      }
    }
  };

  const getFormErrorMessage = (name: keyof SemenFormValues) => {
    return (
      errors[name] && (
        <small className="mt-1 text-sm text-red-500">
          {errors[name]?.message}
        </small>
      )
    );
  };

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const onSubmit = async (data: SemenFormValues) => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      const transformedInput = {
        cane: data.cane || '',
        ownerId: data.owner,
        storageLocationId: data.storageLocation,
        sireId: data.sire?.id,
        ...(isSemenTypeLabelEnabled
          ? {}
          : { sexed: data.sexType !== 'Conventional' }),
        sex: getSexFromSemenSexType(
          data.sexType,
          isSemenConventionalTypeEnabled,
        ),
        cssCertified: data.cssCertified,
        dateReceived:
          data.dateReceived?.toISOString() || new Date().toISOString(),
        colCode: data.colCode,
        naab: data.naab,
        quantityRec: data.quantityReceived || 0,
        quantity: data.quantityReceived || 0,
        center: data.center,
        export: data.export,
        shortName: data.shortName,
        soldTo: data.soldTo,
        secondaryOwner: data.secondaryOwner,
        semenType:
          isSemenConventionalTypeEnabled && data.sexType === 'Conventional'
            ? CONVENTIONAL_SEMEN_TYPE
            : isSemenTypeLabelEnabled
              ? undefined
              : data.semenType,
        units: data.units,
        country: data.country,
        comment: data.comment,
      };

      const res = await addSemen(transformedInput);

      if (res?.success === false) {
        dispatch(
          addToast({
            severity: 'warn',
            summary: t('common.error'),
            detail: res.message || t('semen.failedToCreateSemen'),
            life: ToastLife.MEDIUM,
          }),
        );
      } else {
        dispatch(
          addToast({
            severity: 'success',
            summary: t('common.success'),
            detail: t('semen.semenCreatedSuccessfully'),
            life: ToastLife.MEDIUM,
          }),
        );
        onSuccess?.(data);
        reset();
        onCancel();
      }
    } catch (error) {
      console.error('Failed to create semen:', error);
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail: t('semen.failedToCreateSemen'),
          life: ToastLife.MEDIUM,
        }),
      );
      onError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-6">
      <div className="mb-8">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-bold text-blue-600">
            {t('common.requiredFields')}
          </h3>
        </div>

        <div className="grid grid-cols-2 gap-8">
          <div className="space-y-1">
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label
                  htmlFor="dateReceived"
                  className="text-sm font-medium text-gray-700"
                >
                  {t('semen.dateReceived')}
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-48">
                <Controller
                  name="dateReceived"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Calendar
                      id={field.name}
                      value={field.value}
                      className={classNames('w-full', {
                        'p-invalid': fieldState.invalid,
                      })}
                      onChange={(e) => field.onChange(e.value)}
                      showIcon
                      dateFormat="mm/dd/yy"
                      showOnFocus={false}
                      placeholder="10/7/2025"
                      container={calendarContainer}
                    />
                  )}
                />
                {getFormErrorMessage('dateReceived')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t('sire.sire')}
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-48">
                <Controller
                  name="sire"
                  control={control}
                  render={({ field, fieldState }) => (
                    <AdvancedSireLookup
                      label={null}
                      value={field.value as Sire}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                      onChange={(sire) => field.onChange(sire)}
                      showAddButton={false}
                    />
                  )}
                />
                {getFormErrorMessage('sire')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t('sire.sireShortName')}
                </label>
                <i className="pi pi-lock text-xs text-gray-400"></i>
              </div>
              <div className="w-48">
                <Controller
                  name="shortName"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id={field.name}
                      value={field.value || ''}
                      className="w-full text-sm"
                      disabled
                    />
                  )}
                />
                {getFormErrorMessage('shortName')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t('common.ranch')}
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-48">
                <Controller
                  name="owner"
                  control={control}
                  render={({ field, fieldState }) =>
                    isAlternateAccountLookupEnabled ? (
                      <AdvancedAccountLookup
                        value={field.value as AccountResponse | null}
                        className={classNames('w-full text-sm', {
                          'p-invalid': fieldState.invalid,
                        })}
                        container={calendarContainer}
                        onChange={(account) => field.onChange(account)}
                      />
                    ) : (
                      <AccountAutoComplete
                        id={field.name}
                        value={field.value || ''}
                        className={classNames('w-full text-sm', {
                          'p-invalid': fieldState.invalid,
                        })}
                        onSelect={(e: AutoCompleteSelectEvent) => {
                          field.onChange(e.value);
                        }}
                      />
                    )
                  }
                />
                {getFormErrorMessage('owner')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t('semen.secondaryOwner')}
                </label>
              </div>
              <div className="w-48">
                <Controller
                  name="secondaryOwner"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id={field.name}
                      value={field.value || ''}
                      className="w-full text-sm"
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {getFormErrorMessage('secondaryOwner')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t('semen.soldTo')}
                </label>
              </div>
              <div className="w-48">
                <Controller
                  name="soldTo"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id={field.name}
                      value={field.value || ''}
                      className="w-full text-sm"
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {getFormErrorMessage('soldTo')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t('semen.receivedFrom')}
                </label>
              </div>
              <div className="w-48">
                <Controller
                  name="center"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id={field.name}
                      value={field.value || ''}
                      className="w-full text-sm"
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {getFormErrorMessage('center')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label
                  htmlFor="colCode"
                  className="text-sm font-medium text-gray-700"
                >
                  {t('semen.colCode')}
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-48">
                <Controller
                  name="colCode"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputText
                      id={field.name}
                      value={field.value || ''}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {getFormErrorMessage('colCode')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label
                  htmlFor="quantityReceived"
                  className="text-sm font-medium text-gray-700"
                >
                  {t('semen.qtyRecv')}
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-48">
                <Controller
                  name="quantityReceived"
                  control={control}
                  render={({ field, fieldState }) => (
                    <InputText
                      id={field.name}
                      value={field.value?.toString() || ''}
                      className={classNames('w-full text-sm', {
                        'p-invalid': fieldState.invalid,
                      })}
                      onChange={(e) =>
                        field.onChange(
                          e.target.value ? parseInt(e.target.value) : undefined,
                        )
                      }
                      type="number"
                    />
                  )}
                />
                {getFormErrorMessage('quantityReceived')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t('storageLocation.storageLocation')}
                </label>
                <span className="text-red-500">*</span>
                <i className="pi pi-lock text-xs text-gray-400"></i>
              </div>
              <div className="w-48">
                <Controller
                  name="storageLocation"
                  control={control}
                  render={({ field }) => (
                    <StorageLocationAutoComplete
                      label={null}
                      value={field.value}
                      field="name"
                      onSelect={(e: AutoCompleteSelectEvent) => {
                        handleStorageLocationSelect(e.value);
                        field.onChange(e.value);
                      }}
                      className="w-full"
                    />
                  )}
                />
                {getFormErrorMessage('storageLocation')}
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t('semen.cane')}
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-48">
                <Controller
                  name="cane"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id={field.name}
                      value={field.value || ''}
                      className="w-full text-sm"
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {getFormErrorMessage('cane')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t('semen.naab')}
                </label>
                {isNaabRequired && <span className="text-red-500">*</span>}
              </div>
              <div className="w-48">
                <Controller
                  name="naab"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id={field.name}
                      value={field.value || ''}
                      className="w-full text-sm"
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {getFormErrorMessage('naab')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {isSemenTypeLabelEnabled
                    ? t('semen.semenTypeFieldLabel')
                    : t('semen.sex')}
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-48">
                <Controller
                  name="sexType"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      id={field.name}
                      value={field.value}
                      options={sexTypeOptions}
                      className="w-full text-sm"
                      onChange={(e) => field.onChange(e.value)}
                      placeholder={
                        isSemenTypeLabelEnabled
                          ? t('semen.selectSemenTypeFieldLabel')
                          : t('semen.selectSexType')
                      }
                    />
                  )}
                />
                {getFormErrorMessage('sexType')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t('semen.css')}
                </label>
                <span className="text-red-500">*</span>
              </div>
              <div className="w-48">
                <Controller
                  name="cssCertified"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      id={field.name}
                      value={field.value}
                      options={cssCertifiedOptions}
                      className="w-full text-sm"
                      onChange={(e) => field.onChange(e.value)}
                      placeholder={t('semen.selectCssCertified')}
                    />
                  )}
                />
                {getFormErrorMessage('cssCertified')}
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t('semen.exportStatus')}
                </label>
                {!isConditionalValidation && (
                  <span className="text-red-500">*</span>
                )}
              </div>
              <div className="w-48">
                <Controller
                  name="export"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      inputId={field.name}
                      value={field.value}
                      options={Object.entries(EXPORT_ENUMS).map(
                        ([label, value]) => ({
                          label,
                          value,
                        }),
                      )}
                      onChange={(e) => field.onChange(e.value)}
                      placeholder={t('semen.selectExportStatus')}
                      className="w-full"
                    />
                  )}
                />
                {getFormErrorMessage('export')}
              </div>
            </div>

            {isSemenDataPointsEnabled && (
              <>
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Export Country
                    </label>
                  </div>
                  <div className="w-48">
                    <Controller
                      name="country"
                      control={control}
                      render={({ field }) => (
                        <InputText
                          id={field.name}
                          value={field.value || ''}
                          className="w-full text-sm"
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                    {getFormErrorMessage('country')}
                  </div>
                </div>

                {!isSemenTypeLabelEnabled && (
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-1">
                      <label className="text-sm font-medium text-gray-700">
                        Semen Type
                      </label>
                    </div>
                    <div className="w-48">
                      <Controller
                        name="semenType"
                        control={control}
                        render={({ field }) => (
                          <Dropdown
                            id={field.name}
                            value={field.value}
                            options={SemenTypeOptions}
                            optionLabel="label"
                            optionValue="value"
                            className="w-full text-sm"
                            onChange={(e) => field.onChange(e.value)}
                            placeholder="Select Semen Type"
                          />
                        )}
                      />
                      {getFormErrorMessage('semenType')}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Units
                    </label>
                  </div>
                  <div className="w-48">
                    <Controller
                      name="units"
                      control={control}
                      render={({ field }) => (
                        <InputNumber
                          id="units"
                          value={field.value ?? null}
                          className="w-full"
                          inputClassName="w-full text-sm"
                          onValueChange={(e) =>
                            field.onChange(e.value ?? undefined)
                          }
                          showButtons
                          min={0}
                        />
                      )}
                    />
                    {getFormErrorMessage('units')}
                  </div>
                </div>

                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-1">
                    <label className="text-sm font-medium text-gray-700">
                      Semen Comment
                    </label>
                  </div>
                  <div className="w-48">
                    <Controller
                      name="comment"
                      control={control}
                      render={({ field }) => (
                        <InputText
                          id={field.name}
                          value={field.value || ''}
                          className="w-full text-sm"
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      )}
                    />
                    {getFormErrorMessage('comment')}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <Button
          label={t('common.cancel')}
          icon="pi pi-times"
          severity="secondary"
          onClick={handleCancel}
          type="button"
          disabled={isSubmitting}
          aria-label="Cancel new semen"
        />
        <Button
          label={t('common.save')}
          icon="pi pi-check"
          type="submit"
          loading={isSubmitting}
        />
      </div>
    </form>
  );
};
